import { Controller } from "@hotwired/stimulus"

// Live client-side filtering for long pick-lists that are already fully
// rendered — the media kit's 134 mockup checkboxes, the mockup form's 111
// indie patterns. Everything is in the DOM already, so filtering is a text
// match over data-search plus optional exact matches on data-filter-* keys.
//
// Markup contract:
//   data-controller="record-picker"
//   input  data-record-picker-target="search"  (typing filters)
//   select data-record-picker-target="facet" data-facet-key="category"
//   item   data-record-picker-target="item" data-search="…" data-filter-category="…"
//   any    data-record-picker-target="count"      → "N of M"
//   any    data-record-picker-target="empty"      → shown when nothing matches
//   button data-action="record-picker#selectVisible" / #clearVisible
export default class extends Controller {
  static targets = ["search", "facet", "item", "count", "empty"]

  connect() {
    this.filter()
  }

  filter() {
    const term = (this.hasSearchTarget ? this.searchTarget.value : "").trim().toLowerCase()

    // Only facets with a chosen value constrain the list.
    const activeFacets = this.facetTargets
      .filter((select) => select.value !== "")
      .map((select) => [select.dataset.facetKey, select.value])

    let visible = 0

    this.itemTargets.forEach((item) => {
      const haystack = (item.dataset.search || "").toLowerCase()
      const matchesTerm = term === "" || haystack.includes(term)
      const matchesFacets = activeFacets.every(
        ([key, value]) => (item.dataset[`filter${this.camelize(key)}`] || "") === value
      )

      const show = matchesTerm && matchesFacets
      item.classList.toggle("hidden", !show)
      if (show) visible++
    })

    if (this.hasCountTarget) {
      this.countTarget.textContent = `${visible} of ${this.itemTargets.length}`
    }
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visible > 0)
    }
  }

  reset() {
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this.facetTargets.forEach((select) => { select.value = "" })
    this.filter()
  }

  // Bulk actions apply to what's on screen, so filtering to a category and
  // hitting "select shown" is the fast path for building a kit.
  selectVisible(event) {
    event.preventDefault()
    this.setVisibleChecked(true)
  }

  clearVisible(event) {
    event.preventDefault()
    this.setVisibleChecked(false)
  }

  setVisibleChecked(checked) {
    this.itemTargets.forEach((item) => {
      if (item.classList.contains("hidden")) return
      const box = item.querySelector('input[type="checkbox"]')
      if (box) box.checked = checked
    })
  }

  // data-filter-category → dataset.filterCategory
  camelize(key) {
    return key
      .split(/[-_]/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join("")
  }
}
